import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import HRLayout from '../../components/layout/HRLayout';
import ComplianceIndicator from '../../components/ComplianceIndicator';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import {
  CheckCircle2, XCircle, RotateCcw, Eye, Download, ShieldCheck, Search, ClipboardList,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ComplianceQueuePage({ hrMode = false }) {
  const Layout = hrMode ? HRLayout : AdminLayout;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [reviewModal, setReviewModal] = useState({ open: false, doc: null, kind: null });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState({ open: false, blobUrl: null, mime: null, fileName: null });

  const load = useCallback(async () => {
    try { const res = await axios.get(`${API}/admin/documents/pending-review`); setItems(res.data || []); }
    catch { toast.error('Failed to load queue'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approve = async (doc) => {
    try { await axios.post(`${API}/admin/documents/${doc.id}/approve`); toast.success('Approved'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const submitReview = async () => {
    const { doc, kind } = reviewModal;
    if (kind === 'reject' && !reason.trim()) { toast.error('Reason required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/documents/${doc.id}/${kind === 'reject' ? 'reject' : 'request-reupload'}`, { reason });
      toast.success(kind === 'reject' ? 'Rejected' : 'Re-upload requested');
      setReviewModal({ open: false, doc: null, kind: null }); setReason(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  const openPreview = async (doc) => {
    try {
      const res = await axios.get(`${API}/admin/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      setPreview({ open: true, blobUrl: url, mime: doc.mime_type, fileName: doc.file_name });
    } catch { toast.error('Preview failed'); }
  };
  const closePreview = () => { if (preview.blobUrl) URL.revokeObjectURL(preview.blobUrl); setPreview({ open: false, blobUrl: null, mime: null, fileName: null }); };
  const triggerDownload = async (doc) => {
    try {
      const res = await axios.get(`${API}/admin/documents/${doc.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = doc.file_name || 'document';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };
  const exportXlsx = async () => {
    try {
      const res = await axios.get(`${API}/admin/compliance/queue/export.xlsx`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = url; a.download = 'compliance-queue.xlsx';
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Export failed'); }
  };

  const filtered = items.filter((i) => {
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    return (i.employee_name || '').toLowerCase().includes(q) ||
           (i.employee_email || '').toLowerCase().includes(q) ||
           (i.doc_type_label || '').toLowerCase().includes(q);
  });

  return (
    <Layout>
      <div className="p-4 sm:p-6 space-y-6" data-testid="compliance-queue-page">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-6">
          <div>
            <h1 className="text-2xl font-barlow tracking-widest flex items-center gap-3">
              <ClipboardList className="w-6 h-6 text-primary" /> COMPLIANCE QUEUE
            </h1>
            <p className="text-sm text-muted-foreground mt-1">All documents waiting for admin review across the workforce.</p>
          </div>
          <Button onClick={exportXlsx} variant="outline" className="rounded-sm uppercase tracking-wider text-xs" data-testid="export-queue-btn">
            <Download className="w-3.5 h-3.5 mr-2" /> Export (Excel)
          </Button>
        </div>

        <div className="relative w-full sm:max-w-sm">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by employee, email or doc…" className="pl-8 rounded-sm" data-testid="queue-search" />
        </div>

        {loading ? (
          <div className="py-10 text-center text-muted-foreground animate-pulse">Loading…</div>
        ) : filtered.length === 0 ? (
          <Card className="rounded-sm">
            <CardContent className="p-10 text-center text-muted-foreground">
              <ShieldCheck className="w-10 h-10 mx-auto text-green-400 mb-3" />
              All clear — nothing pending review.
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {filtered.map((d) => (
              <Card key={d.id} className="rounded-sm" data-testid={`queue-item-${d.id}`}>
                <CardContent className="p-4 flex flex-wrap items-center gap-3">
                  <div className="flex-1 min-w-[220px]">
                    <p className="text-sm font-medium flex items-center gap-2 flex-wrap">
                      {d.user_id && <ComplianceIndicator userId={d.user_id} />}
                      {d.employee_name} · <span className="text-muted-foreground">{d.employee_email}</span>
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      <span className="text-foreground">{d.doc_type_label}</span> v{d.version} · {d.file_name}
                      {d.expiry_date && <> · expires {d.expiry_date}</>}
                    </p>
                  </div>
                  <Badge className="text-[10px] uppercase tracking-wider rounded-sm bg-amber-500/15 text-amber-400 border-amber-500/40 border">PENDING</Badge>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => openPreview(d)} className="rounded-sm h-8" data-testid={`preview-${d.id}`}><Eye className="w-3.5 h-3.5 mr-1" /> Preview</Button>
                    <Button size="sm" variant="outline" onClick={() => triggerDownload(d)} className="rounded-sm h-8"><Download className="w-3.5 h-3.5" /></Button>
                    <Button size="sm" onClick={() => approve(d)} className="rounded-sm h-8 bg-green-600 hover:bg-green-500 text-white" data-testid={`approve-${d.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => { setReviewModal({ open: true, doc: d, kind: 'reject' }); setReason(''); }} className="rounded-sm h-8 border-red-500/40 text-red-400 hover:bg-red-500/10"><XCircle className="w-3.5 h-3.5 mr-1" /> Reject</Button>
                    <Button size="sm" variant="outline" onClick={() => { setReviewModal({ open: true, doc: d, kind: 'reupload' }); setReason(''); }} className="rounded-sm h-8 border-amber-500/40 text-amber-400 hover:bg-amber-500/10"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Re-upload</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <Dialog open={reviewModal.open} onOpenChange={(o) => !o && setReviewModal({ open: false, doc: null, kind: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{reviewModal.kind === 'reject' ? 'Reject document' : 'Request re-upload'}</DialogTitle>
            <DialogDescription>This reason is visible to the employee.</DialogDescription>
          </DialogHeader>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} className="rounded-sm" placeholder="e.g. Photo blurry, expiry illegible…" data-testid="queue-review-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewModal({ open: false, doc: null, kind: null })} disabled={saving} className="rounded-sm">Cancel</Button>
            <Button onClick={submitReview} disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-yellow-300" data-testid="confirm-queue-review-btn">
              {saving ? 'Saving…' : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={preview.open} onOpenChange={(o) => !o && closePreview()}>
        <DialogContent className="max-w-4xl h-[80vh] flex flex-col">
          <DialogHeader><DialogTitle>{preview.fileName}</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-auto bg-zinc-950 rounded-sm">
            {preview.blobUrl && preview.mime?.startsWith('image/') && <img src={preview.blobUrl} alt={preview.fileName} className="w-full h-full object-contain" />}
            {preview.blobUrl && preview.mime === 'application/pdf' && <iframe src={preview.blobUrl} title={preview.fileName} className="w-full h-full bg-white" />}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
